/**
 * 用户心跳 Composable
 * 登录后每 10 秒发送一次心跳，维护在线状态
 * 同时检查 token 是否快过期，快过期则自动刷新
 */

import { ref, watch, onUnmounted } from 'vue'
import { useUserStore } from '@/stores'
import { userHeartbeat } from '@/services/api/user'
import { refreshToken, logout } from '@/services/api/auth'
import { isTokenExpired, getRefreshToken } from '@/services/request/client'

// 心跳间隔（毫秒）
const HEARTBEAT_INTERVAL = 10 * 1000 // 10秒

// Token 刷新提前量（秒）- 在过期前多少秒刷新
// isTokenExpired 已经提前 5 分钟检测，这里额外提前 2 分钟刷新
const TOKEN_REFRESH_BUFFER = 120 // 2分钟

// 全局心跳定时器 ID（避免重复创建）
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

// 是否正在刷新 token（防止重复刷新）
let isRefreshing = false

// 是否正在运行
const isRunning = ref(false)

/**
 * 检查是否需要刷新 Token
 * 在 isTokenExpired 基础上再提前 2 分钟
 */
const shouldRefreshToken = (): boolean => {
  const expiresAt = localStorage.getItem('token_expires')
  if (!expiresAt) return false

  const now = Math.floor(Date.now() / 1000)
  // isTokenExpired 提前 5 分钟，这里再提前 2 分钟，共提前 7 分钟刷新
  return now >= parseInt(expiresAt) - 300 - TOKEN_REFRESH_BUFFER
}

/**
 * 尝试刷新 Token
 */
const tryRefreshToken = async (): Promise<boolean> => {
  // 防止重复刷新
  if (isRefreshing) return true

  // 没有 refresh token 则不刷新
  if (!getRefreshToken()) return false

  isRefreshing = true
  console.log('[Heartbeat] Token 即将过期，尝试刷新...')

  try {
    const result = await refreshToken()

    if (result.success) {
      console.log('[Heartbeat] Token 刷新成功')
      return true
    } else {
      console.warn('[Heartbeat] Token 刷新失败:', result.error)
      return false
    }
  } catch (error) {
    console.error('[Heartbeat] Token 刷新异常:', error)
    return false
  } finally {
    isRefreshing = false
  }
}

/**
 * 发送心跳（同时检查并刷新 Token）
 */
const sendHeartbeat = async () => {
  // 先检查 Token 是否需要刷新
  if (shouldRefreshToken()) {
    const refreshed = await tryRefreshToken()
    if (!refreshed && isTokenExpired()) {
      // 刷新失败且 Token 已过期，登出
      console.warn('[Heartbeat] Token 已过期且刷新失败，执行登出')
      const userStore = useUserStore()
      logout() // 清除 token
      await userStore.logout() // 清除用户状态
      stopHeartbeat()
      return
    }
  }

  // 发送心跳
  const result = await userHeartbeat()
  if (!result.success) {
    console.warn('[Heartbeat] 心跳失败:', result.error)
  }
}

/**
 * 启动心跳
 */
const startHeartbeat = () => {
  if (heartbeatTimer) {
    return // 已经在运行
  }

  console.log('[Heartbeat] 启动心跳，间隔:', HEARTBEAT_INTERVAL / 1000, '秒')

  // 立即发送一次
  sendHeartbeat()

  // 定时发送
  heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
  isRunning.value = true
}

/**
 * 停止心跳
 */
const stopHeartbeat = () => {
  if (heartbeatTimer) {
    console.log('[Heartbeat] 停止心跳')
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
    isRunning.value = false
  }
}

/**
 * 用户心跳 Hook
 * 在 App.vue 中调用，自动根据登录状态管理心跳
 */
export function useHeartbeat() {
  const userStore = useUserStore()

  // 监听登录状态变化
  const stopWatch = watch(
    () => userStore.loggedIn,
    loggedIn => {
      if (loggedIn) {
        startHeartbeat()
      } else {
        stopHeartbeat()
      }
    },
    { immediate: true }
  )

  // 监听页面可见性变化
  const handleVisibilityChange = () => {
    if (!userStore.loggedIn) return

    if (document.visibilityState === 'visible') {
      // 页面变为可见，恢复心跳
      if (!heartbeatTimer) {
        startHeartbeat()
      }
    } else {
      // 页面变为不可见，暂停心跳（节省资源）
      stopHeartbeat()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 组件卸载时清理
  onUnmounted(() => {
    stopWatch()
    stopHeartbeat()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    isRunning,
    startHeartbeat,
    stopHeartbeat,
  }
}
